import type React from 'react';
import { useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';

// Configuration constants
const COLOR_PRIMARY = 'black';
const PARTICLE_COUNT = 25;
const NUM_OF_SPIRAL_ARMS = 5;
const SPIRAL_TIGHTNESS = 0.2;
const ROTATION_SPEED = 0.15;
const SPEED_MULTIPLIER = 0.5;

interface Particle {
  angle: number;
  armIndex: number;
  color: {
    b: number;
    g: number;
    r: number;
  };
  distance: number;
  opacity: number;
  size: number;
  speedFactor: number;
}

const SpiralIndicator: React.FC<{ height: number; width: number }> = ({
  height,
  width,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Handle high-DPI screens (Want clear particles)
    const dpr = window.devicePixelRatio || 1;

    // Increase actual pixel buffer so it scales properly
    canvas.width = width * dpr;
    canvas.height = height * dpr;

    // Scale drawing so coordinates remain unchanged
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const centerX = width / 2;
    const centerY = height / 2;

    let time = 0;
    let lastTime = 0;
    let animationFrameId: number;

    // Constrain maxRadius to fit within container with padding for particle size
    const maxRadius = Math.min(width, height) / 2 - 2;

    const particles: Particle[] = [];

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const distanceFactor = Math.pow(Math.random(), 0.25);
      const distance = distanceFactor * maxRadius;

      const armIndex = Math.floor(Math.random() * NUM_OF_SPIRAL_ARMS);
      const armOffset = (armIndex / NUM_OF_SPIRAL_ARMS) * Math.PI * 2;

      const safeDistance = Math.max(distance, 5); // avoid log(0)
      const spiralAngle = Math.log(safeDistance / 5) / SPIRAL_TIGHTNESS;

      // Create particle with different configurations
      particles.push({
        angle: spiralAngle + armOffset,
        armIndex,
        color: {
          b: 255,
          g: 220 + Math.floor(Math.random() * 35),
          r: 220 + Math.floor(Math.random() * 35),
        },
        distance,
        opacity: 0.3 + Math.random() * 0.7,
        size: 1 + Math.random() * 0.5,
        speedFactor: 0.5 + Math.random() * SPEED_MULTIPLIER,
      });
    }

    const render = (timestamp: number) => {
      if (!lastTime) lastTime = timestamp;
      const deltaTime = timestamp - lastTime;
      lastTime = timestamp;
      time += deltaTime * 0.001;

      // Clear - because we scaled with setTransform
      ctx.clearRect(0, 0, width, height);

      // Center dot
      ctx.beginPath();
      ctx.arc(centerX, centerY, 3, 0, Math.PI * 2);
      ctx.fillStyle = COLOR_PRIMARY;
      ctx.fill();

      for (const particle of particles) {
        const rotationFactor =
          1 / Math.sqrt(Math.max(particle.distance / 10, 0.1));

        particle.angle +=
          ROTATION_SPEED *
          rotationFactor *
          particle.speedFactor *
          deltaTime *
          0.05;

        const x = centerX + Math.cos(particle.angle) * particle.distance;
        const y = centerY + Math.sin(particle.angle) * particle.distance;

        // Skip rendering if particle is outside canvas bounds
        if (x < 0 || x > width || y < 0 || y > height) {
          continue;
        }

        const armPhase =
          (time * 0.5 + particle.armIndex / NUM_OF_SPIRAL_ARMS) % 1;
        const pulseFactor = Math.sin(armPhase * Math.PI * 2) * 0.3 + 0.7;

        ctx.beginPath();
        ctx.arc(x, y, particle.size * pulseFactor, 0, Math.PI * 2);
        ctx.fillStyle = COLOR_PRIMARY;
        ctx.fill();

        // Trails for larger particles
        if (particle.size > 1.8) {
          const trailLength = particle.distance * 0.15;
          const trailAngle = particle.angle - 0.1 * rotationFactor;
          const trailX =
            centerX + Math.cos(trailAngle) * (particle.distance - trailLength);
          const trailY =
            centerY + Math.sin(trailAngle) * (particle.distance - trailLength);

          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(trailX, trailY);
          ctx.strokeStyle = COLOR_PRIMARY;
          ctx.lineWidth = particle.size * 0.5;
          ctx.stroke();
        }
      }

      animationFrameId = window.requestAnimationFrame(render);
    };

    animationFrameId = window.requestAnimationFrame(render);

    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [height, width]);

  return (
    <AnimationContainer height={height} width={width}>
      <CircleContainer height={height} width={width}>
        <StyledCanvas height={height} ref={canvasRef} width={width} />
      </CircleContainer>
    </AnimationContainer>
  );
};

const AnimationContainer = styled('div')<{
  height: number;
  width: number;
}>`
  position: relative;
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin-left: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  transition: border-color 0.3s ease;
  border-radius: 50%;

  &:hover {
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const CircleContainer = styled('div')<{
  height: number;
  width: number;
}>`
  position: relative;
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

// Keep CSS size in layout pixels
const StyledCanvas = styled('canvas')<{
  height: number;
  width: number;
}>`
  position: absolute;
  left: 0;
  top: 0;
  width: ${({ width }) => width}px;
  height: ${({ height }) => height}px;
`;

export default SpiralIndicator;
